import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Loader2, Copy } from 'lucide-react';

const input = 'w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500';

export const PayPalChargeDialog = ({ matter, open, onOpenChange }) => {
  const { api } = useAuth();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ concept: '', amount_usd: '', amount_clp_ref: '', send_email: true });
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/paypal/charges', { params: { matter_id: matter.id } }).then((r) => setItems(r.data.charges)).catch(() => {});
  useEffect(() => {
    api.get('/paypal/status').then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false }));
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post('/paypal/charges', {
        client_id: matter.client_id, matter_id: matter.id, concept: form.concept, amount_usd: Number(form.amount_usd),
        amount_clp_ref: form.amount_clp_ref ? Number(form.amount_clp_ref) : null, send_email: form.send_email,
      });
      toast.success(r.data.emailed ? 'Cobro enviado por correo al cliente' : 'Cobro creado: copia el enlace de pago');
      setForm({ concept: '', amount_usd: '', amount_clp_ref: '', send_email: form.send_email });
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? 'Revisa el concepto (mín. 3 letras) y el monto (USD 1 a 10.000)' : d || 'No se pudo crear el cobro');
    } finally { setBusy(false); }
  };
  const copy = (url) => navigator.clipboard.writeText(url).then(() => toast.success('Enlace copiado'));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="paypal-charge-dialog" className="bg-slate-950 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white">Cobrar con PayPal</DialogTitle>
          <DialogDescription className="text-slate-400">El cliente recibe un enlace para pagar con PayPal o tarjeta. PayPal cobra en USD.</DialogDescription>
        </DialogHeader>
        {status && !status.configured && (
          <p data-testid="paypal-not-configured" className="text-xs bg-slate-800 border border-slate-600 text-slate-300 rounded-lg px-3 py-2">Falta conectar tu cuenta PayPal (Client ID y Secret). Los cobros se activarán al configurarla.</p>
        )}
        {status?.mode === 'sandbox' && <p data-testid="paypal-sandbox-note" className="text-xs text-amber-300">Modo prueba: los pagos no mueven dinero real.</p>}
        <div className="space-y-2">
          <input data-testid="paypal-concept" value={form.concept} onChange={(e) => setForm({ ...form, concept: e.target.value })} placeholder="Concepto (ej. Ratificación de finiquito)" className={input} />
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="paypal-amount-usd" type="number" min="1" step="0.01" value={form.amount_usd} onChange={(e) => setForm({ ...form, amount_usd: e.target.value })} placeholder="Monto USD" className={input} />
            <input data-testid="paypal-amount-clp" type="number" min="0" value={form.amount_clp_ref} onChange={(e) => setForm({ ...form, amount_clp_ref: e.target.value })} placeholder="Referencia CLP (opcional)" className={input} />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300"><input data-testid="paypal-send-email" type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} />Enviar el cobro al email del cliente</label>
          <button data-testid="paypal-create-charge-btn" onClick={create} disabled={busy || !status?.configured || !form.concept || !form.amount_usd} className="w-full bg-[#ffc439] hover:bg-[#f2b92f] text-slate-900 font-bold rounded-lg py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-40">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}Crear cobro PayPal
          </button>
        </div>
        <ul data-testid="paypal-charges-list" className="space-y-2">
          {items.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-2 text-xs bg-slate-900 border border-slate-800 rounded-lg px-3 py-2">
              <span className="min-w-0 truncate">{c.concept} · USD {c.amount_usd.toFixed(2)}</span>
              <span className="flex items-center gap-2 shrink-0">
                <span className={c.status === 'pagado' ? 'text-emerald-300' : 'text-amber-300'}>{c.status}</span>
                {c.status !== 'pagado' && <button data-testid={`paypal-copy-${c.id}`} onClick={() => copy(c.pay_url)} aria-label="Copiar enlace"><Copy className="w-3.5 h-3.5 text-slate-400" /></button>}
              </span>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
};
